import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getTagStats, getWeeklyDistribution, getWritingStreak, getImportanceDistribution, getAllUserThoughts, getMilestoneThoughts } from '../services/analyticsService';
import { generateThoughtSummary } from '../services/openAiService';
import { colors } from '../styles/styles';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

const Analytics = () => {
  const [timeframe, setTimeframe] = useState(30); // Default to 30 days
  const [loading, setLoading] = useState(true);
  const [recentThoughts, setRecentThoughts] = useState([]);
  const [tagStats, setTagStats] = useState([]);
  const [dailyDistribution, setDailyDistribution] = useState([]);
  const [importanceDistribution, setImportanceDistribution] = useState([]);
  const [milestoneThoughts, setMilestoneThoughts] = useState([]);
  const [streak, setStreak] = useState(0);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Query Builder States
  const [selectedTags, setSelectedTags] = useState([]);
  const [insightFilter, setInsightFilter] = useState('');
  const [filteredThoughts, setFilteredThoughts] = useState([]);

  // Dropdown states
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const tagsDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(event.target)) {
        setIsTagsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup milestone tooltip on unmount
  useEffect(() => {
    return () => {
      const tooltipEl = document.getElementById('milestone-tooltip');
      if (tooltipEl) {
        tooltipEl.remove();
      }
    };
  }, []);

  // Toggle tag selection
  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  // Apply filters to thoughts
  const applyFilters = useCallback(() => {
    let filtered = [...recentThoughts];

    // Filter by tags (using OR logic)
    if (selectedTags.length > 0) {
      filtered = filtered.filter(thought => 
        thought.tags && 
        thought.tags.some(tag => selectedTags.includes(tag))
      );
    }

    // Filter by insight text
    if (insightFilter) {
      filtered = filtered.filter(thought =>
        thought.thought && 
        thought.thought.toLowerCase().includes(insightFilter.toLowerCase())
      );
    }

    setFilteredThoughts(filtered);
  }, [recentThoughts, selectedTags, insightFilter]);

  // Effect to apply filters when dependencies change
  useEffect(() => {
    if (!loading) {
      applyFilters();
    }
  }, [loading, applyFilters]);

  // Export filtered thoughts as CSV
  const exportToCSV = () => {
    const headers = ['Timestamp', 'Thought', 'Tags', 'Importance'];
    const csvContent = [
      headers.join(','),
      ...filteredThoughts.map(thought => [
        thought.timestamp,
        `"${thought.thought?.replace(/"/g, '""') || ''}"`,
        thought.tags?.join(';') || '',
        thought.importance || 0
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `thoughts_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Function to generate thought summary
  const handleGenerateSummary = async () => {
    if (recentThoughts.length === 0) return;
    
    setSummaryLoading(true);
    try {
      const validThoughts = recentThoughts.filter(thought => 
        thought && thought.thought && typeof thought.thought === 'string' && thought.thought.trim() !== ''
      );
      
      if (validThoughts.length > 0) {
        const thoughtSummary = await generateThoughtSummary(validThoughts, timeframe);
        setSummary(thoughtSummary);
      }
    } catch (error) {
      console.error('Error generating thought summary:', error);
    } finally {
      setSummaryLoading(false);
    }
  };

  // Fetch analytics data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Get all thoughts and filter for timeframe locally
        const allThoughts = await getAllUserThoughts();
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - timeframe);
        const cutoffTimestamp = cutoffDate.toISOString();
        
        const recentThoughts = allThoughts.filter(thought => 
          thought.timestamp && thought.timestamp >= cutoffTimestamp
        );
        
        // Process tag stats and distribution in parallel
        await Promise.all([
          (async () => {
            const tags = await getTagStats(recentThoughts);
            setTagStats(tags);
          })(),
          (async () => {
            const distribution = await getWeeklyDistribution(recentThoughts, timeframe);
            setDailyDistribution(distribution);
          })(),
          (async () => {
            const currentStreak = await getWritingStreak(recentThoughts);
            setStreak(currentStreak);
          })(),
          (async () => {
            const importance = await getImportanceDistribution(allThoughts, timeframe);
            setImportanceDistribution(importance);
          })(),
          (async () => {
            const milestones = await getMilestoneThoughts(allThoughts, timeframe);
            console.log(milestones);
            setMilestoneThoughts(milestones);
          })()
        ]);

        // Set all state at once after everything is ready
        setRecentThoughts(recentThoughts);
        setFilteredThoughts(recentThoughts); // Initialize filtered thoughts with all thoughts
        setSummary(null);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [timeframe]);
  
  // Calculate simple stats
  const totalThoughtCount = recentThoughts.length;
  const averagePerDay = Math.round((totalThoughtCount / timeframe) * 10) / 10;
  const taggedThoughtsCount = recentThoughts.filter(t => t.tags && t.tags.length > 0).length;
  const taggedPercentage = totalThoughtCount > 0 
    ? Math.round((taggedThoughtsCount / totalThoughtCount) * 100) 
    : 0;
  
  return (
    <div
      style={{
        maxWidth: '1000px',
        margin: '0 auto',
        paddingTop: '80px',
        animation: 'fadeIn 0.3s cubic-bezier(0.2, 0, 0, 1)',
      }}
    >
      {/* Timeframe Selector */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '24px',
      }}>
        <div
          style={{
            display: 'flex',
            borderRadius: '8px',
            overflow: 'hidden',
            border: `1px solid ${colors.divider}`,
          }}
        >
          {[
            { value: 7, label: '7d' },
            { value: 14, label: '14d' },
            { value: 30, label: '30d' },
            { value: 90, label: '90d' },
          ].map((option, index) => (
            <button
              key={option.value}
              onClick={() => setTimeframe(option.value)}
              style={{
                padding: '8px 10px',
                background: timeframe === option.value ? colors.accent.white : 'transparent',
                color: timeframe === option.value ? 'black' : colors.textSecondary,
                border: 'none',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                margin: 0,
                minWidth: '50px',
                borderLeft: index > 0 ? `1px solid ${colors.divider}` : 'none',
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px', 
        }}>
        </div>
      ) : (
        <div style={{ animation: 'fadeIn 0.3s cubic-bezier(0.2, 0, 0, 1)' }}>
          {/* Net Growth Chart */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ 
              fontSize: '1.2rem', 
              fontWeight: 500, 
              color: colors.textPrimary,
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
            }}>
              <svg 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg" 
                style={{ marginRight: '8px' }}
              >
                <path d="M6 3h12l4 6-10 13L2 9Z" stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M11 3 8 9l4 13 4-13-3-6" stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 9h20" stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Net Growth
            </h2>
            
            <div style={{ 
              borderRadius: '8px',
              padding: '16px',
              border: `1px solid ${colors.divider}`,
            }}>
              {importanceDistribution.length > 0 ? (
                <div style={{ height: '220px' }}>
                  <ChartJsLineChart 
                    data={importanceDistribution} 
                    valueKey="cumulativeImportance"
                    label="Net Growth"
                    tooltipLabel=""
                    showPoints={false}
                    useStandardizedYAxis={true}
                    milestones={milestoneThoughts}
                  />
                </div>
              ) : (
                <div style={{ 
                  padding: '32px 16px',
                  textAlign: 'center',
                  color: colors.textSecondary,
                }}>
                  No importance data for the selected period
                </div>
              )}
            </div>
          </div>

          {/* AI Summary Section */}
          {(totalThoughtCount > 0) && (
            <div style={{ marginBottom: '32px' }}>
              <div style={{ 
                fontSize: '1.2rem', 
                fontWeight: 500, 
                color: colors.textPrimary,
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <h2 style={{
                  fontSize: '1.2rem',
                  fontWeight: 500,
                  margin: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  <svg 
                    width="18" 
                    height="18" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    xmlns="http://www.w3.org/2000/svg" 
                    style={{ marginRight: '8px' }}
                  >
                    <path d="M10 18v-7" stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M11.12 2.198a2 2 0 0 1 1.76.006l7.866 3.847c.476.233.31.949-.22.949H3.474c-.53 0-.695-.716-.22-.949z" stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 18v-7" stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M18 18v-7" stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 22h18" stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6 18v-7" stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Balance Sheet
                </h2>
                <button
                  onClick={handleGenerateSummary}
                  disabled={summaryLoading || recentThoughts.length === 0}
                  style={{
                    background: colors.accent.white,
                    color: 'black',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: summaryLoading || recentThoughts.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: summaryLoading || recentThoughts.length === 0 ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {summaryLoading ? 'Generating...' : 'Generate Insights'}
                </button>
              </div>
              
              {(summaryLoading || summary) && (
                <div style={{ 
                  borderRadius: '8px',
                  padding: '20px',
                  border: `1px solid ${colors.divider}`,
                }}>
                  {summaryLoading ? (
                    <div style={{ 
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '30px 0',
                    }}>
                      <div style={{ color: colors.textSecondary, fontSize: '14px' }}>
                        Analyzing your thoughts...
                      </div>
                    </div>
                  ) : summary ? (
                    <div style={{ 
                      color: colors.textPrimary,
                      fontSize: '15px',
                      lineHeight: '1.6',
                      whiteSpace: 'pre-line',
                    }}>
                      {summary}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          )}
          
          {/* Stats Overview Cards */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
          }}>
            <StatCard 
              title="New Insights" 
              value={totalThoughtCount} 
              subvalue={`${averagePerDay} per day`}
            />
            <StatCard 
              title="Resolution Rate" 
              value={`${taggedPercentage}%`} 
              subvalue={`${taggedThoughtsCount} thoughts`}
            />
            <StatCard 
              title="Current Streak" 
              value={streak} 
              subvalue="days"
            />
          </div>
          
          {/* Tags Section */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ 
              fontSize: '1.2rem', 
              fontWeight: 500, 
              color: colors.textPrimary,
              marginBottom: '16px',
            }}>
              Growth by Area
            </h2>
            
            {tagStats.length === 0 ? (
              <div style={{ 
                padding: '16px', 
                border: `1px solid ${colors.divider}`,
                borderRadius: '8px',
                color: colors.textSecondary,
                fontSize: '14px',
                textAlign: 'center',
              }}>
                No tags used in this period
              </div>
            ) : (
              <div style={{ 
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '16px',
                padding: '1px', // Prevents margin collapse
              }}>
                {tagStats.slice(0, 12).map(stat => (
                  <div 
                    key={stat.tag}
                    style={{
                      borderRadius: '12px',
                      border: `1px solid ${colors.divider}`,
                      backgroundColor: colors.surface,
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      transition: 'all 0.2s ease',
                      ':hover': {
                        backgroundColor: colors.surfaceElevated,
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    {/* Header: Tag Icon + Name */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}>
                      {/* Tag Icon/Avatar */}
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: `linear-gradient(45deg, ${colors.accent.purple}22, ${colors.accent.purple}44)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: colors.accent.purple,
                        fontSize: '13px',
                        fontWeight: '600',
                        letterSpacing: '0.5px',
                        flexShrink: 0,
                      }}>
                        {stat.tag.substring(0, 2).toUpperCase()}
                      </div>

                      {/* Tag Name */}
                      <div style={{
                        fontSize: '15px',
                        fontWeight: '500',
                        color: colors.textPrimary,
                      }}>
                        {stat.tag}
                      </div>
                    </div>

                    {/* Growth Metric */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: colors.textPrimary,
                      fontSize: '15px',
                      fontWeight: '500',
                      marginTop: 'auto', // Push to bottom of card
                    }}>
                      <svg 
                        width="14" 
                        height="14" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke={colors.accent.white}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="m5 12 7-7 7 7"/>
                        <path d="M12 19V5"/>
                      </svg>
                      <span style={{ color: colors.accent.green }}>
                        {stat.importance}
                      </span>
                      <span style={{ 
                        color: colors.textSecondary,
                        fontSize: '13px',
                        fontWeight: 'normal',
                      }}>
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Daily Distribution Chart.js Chart */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ 
              fontSize: '1.2rem', 
              fontWeight: 500, 
              color: colors.textPrimary,
              marginBottom: '16px',
            }}>
              Cognitive Volume
            </h2>
            
            <div style={{ 
              borderRadius: '8px',
              padding: '16px',
              border: `1px solid ${colors.divider}`,
            }}>
              {dailyDistribution.length > 0 ? (
                <div style={{ height: '220px' }}>
                  <ChartJsLineChart 
                    data={dailyDistribution}
                    showPoints={true}
                  />
                </div>
              ) : (
                <div style={{ 
                  padding: '32px 16px',
                  textAlign: 'center',
                  color: colors.textSecondary,
                }}>
                  No data for the selected period
                </div>
              )}
            </div>
          </div>

          {/* Query Builder Section */}
          <div>
            <h2 style={{ 
              fontSize: '1.2rem', 
              fontWeight: 500, 
              color: colors.textPrimary,
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" stroke={colors.accent.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Query Builder
            </h2>
            
            <div style={{ 
              borderRadius: '8px',
              padding: '20px',
              border: `1px solid ${colors.divider}`,
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              {/* Tags Filter */}
              <div ref={tagsDropdownRef}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px',
                  color: colors.textSecondary,
                  fontSize: '14px'
                }}>
                  Tags
                </label>
                <div style={{ position: 'relative' }}>
                  <div
                    onClick={() => setIsTagsDropdownOpen(!isTagsDropdownOpen)}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      borderRadius: '6px',
                      backgroundColor: 'transparent',
                      border: `1px solid ${colors.divider}`,
                      color: colors.textPrimary,
                      fontSize: '14px',
                      cursor: 'pointer',
                      minHeight: '42px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '6px',
                      alignItems: 'center',
                      justifyContent: 'flex-start'
                    }}
                  >
                    {selectedTags.length > 0 ? (
                      selectedTags.map(tag => (
                        <span
                          key={tag}
                          style={{
                            backgroundColor: colors.accent.purple + '33',
                            color: colors.accent.white,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            height: '24px'
                          }}
                        >
                          {tag}
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTag(tag);
                            }}
                            style={{
                              cursor: 'pointer',
                              opacity: 0.7,
                              ':hover': { opacity: 1 }
                            }}
                          >
                            ×
                          </span>
                        </span>
                      ))
                    ) : (
                      <span style={{ 
                        color: colors.textTertiary,
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        Select tags...
                      </span>
                    )}
                  </div>
                  
                  {isTagsDropdownOpen && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '4px',
                      backgroundColor: colors.surfaceElevated,
                      border: `1px solid ${colors.divider}`,
                      borderRadius: '6px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 10,
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}>
                      {tagStats.length > 0 ? (
                        <div style={{
                          display: 'grid',
                          gridTemplateRows: `repeat(${Math.ceil(tagStats.length / 4)}, min-content)`, // Updated for 4 columns
                          gridAutoFlow: 'column',
                          gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', // Changed to 4 columns and slightly reduced min width
                          gap: '2px',
                          padding: '4px',
                          maxHeight: '300px',
                          overflowY: 'auto'
                        }}>
                          {tagStats.map(stat => (
                            <div
                              key={stat.tag}
                              onClick={() => toggleTag(stat.tag)}
                              style={{
                                padding: '6px 8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: selectedTags.includes(stat.tag) ? colors.accent.purple + '33' : 'transparent',
                                borderRadius: '4px',
                                transition: 'background-color 0.15s ease',
                                ':hover': {
                                  backgroundColor: colors.accent.purple + '22'
                                }
                              }}
                            >
                              <div style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                gap: '8px'
                              }}>
                                <span style={{ 
                                  color: colors.textPrimary,
                                  fontSize: '13px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {stat.tag}
                                </span>
                                <span style={{ 
                                  color: colors.textSecondary,
                                  fontSize: '11px',
                                  backgroundColor: colors.surface,
                                  padding: '1px 6px',
                                  borderRadius: '10px',
                                  minWidth: '24px',
                                  textAlign: 'center'
                                }}>
                                  {stat.count}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{
                          padding: '8px 12px',
                          color: colors.textSecondary,
                          textAlign: 'center',
                          fontSize: '13px'
                        }}>
                          No tags available
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Insight Text Filter */}
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px',
                  color: colors.textSecondary,
                  fontSize: '14px'
                }}>
                  Search Text
                </label>
                <input
                  type="text"
                  value={insightFilter}
                  onChange={(e) => setInsightFilter(e.target.value)}
                  placeholder="Search in thoughts..."
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '6px',
                    backgroundColor: 'transparent',
                    border: `1px solid ${colors.divider}`,
                    color: colors.textPrimary,
                    fontSize: '14px'
                  }}
                />
              </div>

              {/* Results and Export */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '8px'
              }}>
                <div style={{
                  color: colors.textSecondary,
                  fontSize: '14px'
                }}>
                  {filteredThoughts.length} thoughts found
                </div>
                <button
                  onClick={exportToCSV}
                  disabled={filteredThoughts.length === 0}
                  style={{
                    background: colors.accent.white,
                    color: 'black',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: filteredThoughts.length === 0 ? 'not-allowed' : 'pointer',
                    opacity: filteredThoughts.length === 0 ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="7 10 12 15 17 10"/>
                    <line x1="12" y1="15" x2="12" y2="3"/>
                  </svg>
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS for iOS-like button press effect */}
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes ios-tap-pulse {
            0% { opacity: 1; transform: scale(1); }
            35% { opacity: 0.4; transform: scale(0.97); }
            100% { opacity: 1; transform: scale(1); }
          }
          
          .ios-button {
            -webkit-tap-highlight-color: transparent;
          }
          
          .ios-button:active {
            animation: ios-tap-pulse 180ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
          }
          
          .ios-tapped {
            animation: ios-tap-pulse 180ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
          }
        `}
      </style>
    </div>
  );
};

// Chart.js Line Chart Component
const ChartJsLineChart = ({ 
  data, 
  valueKey = 'count', 
  label = 'Thoughts', 
  tooltipLabel = 'thoughts', 
  showPoints = true,
  useStandardizedYAxis = false,
  milestones = []
}) => {
  const chartData = {
    labels: data.map(item => item.displayDate),
    datasets: [
      {
        label: label,
        data: data.map(item => item[valueKey]),
        fill: false,
        backgroundColor: colors.accent.white,
        borderColor: colors.accent.white,
        tension: 0.1,
        pointRadius: showPoints ? 4 : 0,
        pointHoverRadius: showPoints ? 6 : 0,
        pointBackgroundColor: colors.accent.white,
        pointBorderWidth: 0,
      },
    ],
  };

  const calculateStandardizedStepSize = (values) => {
    const max = Math.max(...values);
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
    const normalized = max / magnitude;
    
    let stepSize;
    if (normalized <= 2) stepSize = magnitude / 4;
    else if (normalized <= 5) stepSize = magnitude / 2;
    else stepSize = magnitude;
    
    return stepSize;
  };

  // Create milestone annotations
  const annotations = {};
  milestones.forEach((milestone, index) => {
    // Find the index of this milestone's date in our chart data
    const dateIndex = data.findIndex(item => 
      item.date === milestone.date || item.displayDate === milestone.displayDate
    );

    console.log(dateIndex);
    
    if (dateIndex !== -1) {
      annotations[`milestone-${index}`] = {
        type: 'line',
        mode: 'vertical',
        scaleID: 'x',
        value: dateIndex,
        borderColor: `${colors.divider}CC`,
        borderWidth: 1,
        borderDash: [5, 5]
      };
    }
  });

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: `${colors.divider}88`,
          drawBorder: false,
        },
        ticks: {
          color: colors.textTertiary,
          font: {
            size: 10,
          },
          padding: 8,
          stepSize: useStandardizedYAxis 
            ? calculateStandardizedStepSize(data.map(item => item[valueKey]))
            : Math.max(1, Math.ceil(Math.max(...data.map(item => item[valueKey])) / 4)),
          callback: function(value) {
            if (useStandardizedYAxis) {
              return value.toLocaleString();
            }
            return value;
          }
        },
        border: {
          display: false,
        },
      },
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: colors.textTertiary,
          font: {
            size: 10,
          },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 7,
        },
        border: {
          display: false,
        },
      },
    },
    plugins: {
      annotation: {
        annotations: annotations
      },
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: colors.surfaceElevated,
        titleColor: colors.textPrimary,
        bodyColor: colors.textSecondary,
        titleFont: {
          size: 12,
          weight: 'bold',
        },
        bodyFont: {
          size: 12,
        },
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => {
            return tooltipItems[0].label;
          },
          label: (context) => {
            const value = context.parsed.y.toLocaleString();
            return tooltipLabel ? `${value} ${tooltipLabel}` : value;
          },
        },
        // Custom tooltip for milestone lines
        external: function(context) {
          // Check if we're hovering over a milestone annotation
          const tooltip = context.tooltip;
          if (tooltip.dataPoints && tooltip.dataPoints.length > 0) {
            const dataPoint = tooltip.dataPoints[0];
            const dataIndex = dataPoint.dataIndex;
            
            // Find matching milestone for this data point
            const matchingMilestone = milestones.find(milestone => {
              const milestoneIndex = data.findIndex(item => 
                item.date === milestone.date || item.displayDate === milestone.displayDate
              );
              return milestoneIndex === dataIndex;
            });
            
            if (matchingMilestone) {
              // Show milestone tooltip
              let tooltipEl = document.getElementById('milestone-tooltip');
              if (!tooltipEl) {
                tooltipEl = document.createElement('div');
                tooltipEl.id = 'milestone-tooltip';
                tooltipEl.style.cssText = `
                  position: absolute;
                  pointer-events: none;
                  opacity: 0;
                  transition: opacity 0.2s ease;
                  background: ${colors.surfaceElevated};
                  border: 1px solid ${colors.divider};
                  border-radius: 8px;
                  padding: 12px;
                  font-size: 13px;
                  color: ${colors.textPrimary};
                  max-width: 300px;
                  z-index: 1000;
                  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                `;
                document.body.appendChild(tooltipEl);
              }
              
              tooltipEl.innerHTML = `
                <div style="font-weight: 600; margin-bottom: 6px; color: ${colors.accent.purple};">
                  ${matchingMilestone.displayDate}
                </div>
                <div style="line-height: 1.4;">
                  ${matchingMilestone.thought}
                </div>
              `;
              
              const rect = context.chart.canvas.getBoundingClientRect();
              tooltipEl.style.left = rect.left + tooltip.caretX + 'px';
              tooltipEl.style.top = rect.top + tooltip.caretY - tooltipEl.offsetHeight - 10 + 'px';
              tooltipEl.style.opacity = '1';
            }
          } else {
            // Hide milestone tooltip
            const tooltipEl = document.getElementById('milestone-tooltip');
            if (tooltipEl) {
              tooltipEl.style.opacity = '0';
            }
          }
        }
      },
    },
    interaction: {
      intersect: false,
      mode: 'index',
    },
    onHover: (event, activeElements, chart) => {
      // Handle milestone line hover
      const points = chart.getElementsAtEventForMode(event, 'nearest', { intersect: false }, true);
      
      if (points.length > 0) {
        const point = points[0];
        const dataIndex = point.index;
        
        // Check if there's a milestone at this data index
        const matchingMilestone = milestones.find(milestone => {
          const milestoneIndex = data.findIndex(item => 
            item.date === milestone.date || item.displayDate === milestone.displayDate
          );
          return milestoneIndex === dataIndex;
        });
        
        if (matchingMilestone) {
          chart.canvas.style.cursor = 'pointer';
          
          // Show milestone tooltip
          let tooltipEl = document.getElementById('milestone-tooltip');
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'milestone-tooltip';
            tooltipEl.style.cssText = `
              position: absolute;
              pointer-events: none;
              opacity: 0;
              transition: opacity 0.2s ease;
              background: ${colors.surfaceElevated};
              border: 1px solid ${colors.divider};
              border-radius: 8px;
              padding: 12px;
              font-size: 13px;
              color: ${colors.textPrimary};
              max-width: 300px;
              z-index: 1000;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            `;
            document.body.appendChild(tooltipEl);
          }
          
          tooltipEl.innerHTML = `
            <div style="font-weight: 600; margin-bottom: 6px; color: ${colors.accent.purple};">
              Milestone - ${matchingMilestone.displayDate}
            </div>
            <div style="line-height: 1.4;">
              ${matchingMilestone.thought}
            </div>
          `;
          
          const rect = chart.canvas.getBoundingClientRect();
          const canvasPosition = event.native || event;
          tooltipEl.style.left = rect.left + canvasPosition.offsetX + 15 + 'px';
          tooltipEl.style.top = rect.top + canvasPosition.offsetY - tooltipEl.offsetHeight - 10 + 'px';
          tooltipEl.style.opacity = '1';
        } else {
          chart.canvas.style.cursor = 'default';
          // Hide milestone tooltip
          const tooltipEl = document.getElementById('milestone-tooltip');
          if (tooltipEl) {
            tooltipEl.style.opacity = '0';
          }
        }
      } else {
        chart.canvas.style.cursor = 'default';
        // Hide milestone tooltip
        const tooltipEl = document.getElementById('milestone-tooltip');
        if (tooltipEl) {
          tooltipEl.style.opacity = '0';
        }
      }
    }
  };

  return <Line data={chartData} options={options} />;
};

// Stat Card Component
const StatCard = ({ title, value, subvalue }) => {
  return (
    <div style={{
      borderRadius: '8px',
      padding: '16px',
      display: 'flex',
      flexDirection: 'column',
      border: `1px solid ${colors.divider}`,
    }}>
      <div style={{ 
        fontSize: '14px',
        color: colors.textSecondary,
        marginBottom: '8px',
      }}>
        {title}
      </div>
      <div style={{ 
        fontSize: '28px',
        fontWeight: '600',
        color: colors.textPrimary,
        marginBottom: '4px',
      }}>
        {value}
      </div>
      {subvalue && (
        <div style={{ 
          fontSize: '12px',
          color: colors.textTertiary,
        }}>
          {subvalue}
        </div>
      )}
    </div>
  );
};

export default Analytics; 